import React, { useRef, useState, useEffect, useCallback } from 'react'

import { prepCanvas } from './canvasUtil'

interface Props { title?: string; description?: string; onComplete?: () => void }

// Lực Coulomb (Lớp 11 — Bài 11): F = k·|q1·q2|/r². Kéo hai điện tích để đổi khoảng cách;
// cùng dấu → đẩy, trái dấu → hút. Vector lực vẽ trên mỗi điện tích.
const CoulombForceSimulation: React.FC<Props> = ({
  title = 'Lực tương tác Coulomb',
  description = 'F = k·|q₁·q₂|/r². Kéo điện tích để đổi khoảng cách r; đổi dấu/độ lớn điện tích để thấy lực hút/đẩy.',
  onComplete,
}) => {
  const cv = useRef<HTMLCanvasElement>(null)
  const p1 = useRef({ x: 180, y: 150 })
  const p2 = useRef({ x: 460, y: 150 })
  const drag = useRef<null | 1 | 2>(null)
  const [q1, setQ1] = useState(3)
  const [q2, setQ2] = useState(-3)
  const [rVal, setRVal] = useState(280)
  const [, force] = useState(0)
  const firstRef = useRef(false)
  const W = 640, H = 300
  const K = 90000 // hằng số hiển thị

  const arrow = (ctx: CanvasRenderingContext2D, x: number, y: number, dx: number, dy: number, color: string) => {
    const len = Math.hypot(dx, dy); if (len < 1) return
    const ex = x + dx, ey = y + dy
    ctx.strokeStyle = color; ctx.fillStyle = color; ctx.lineWidth = 4; ctx.lineCap = 'round'
    ctx.beginPath(); ctx.moveTo(x, y); ctx.lineTo(ex, ey); ctx.stroke()
    const a = Math.atan2(dy, dx)
    ctx.beginPath(); ctx.moveTo(ex, ey); ctx.lineTo(ex - 14 * Math.cos(a - 0.42), ey - 14 * Math.sin(a - 0.42)); ctx.lineTo(ex - 14 * Math.cos(a + 0.42), ey - 14 * Math.sin(a + 0.42)); ctx.closePath(); ctx.fill()
  }
  const sphere = (ctx: CanvasRenderingContext2D, x: number, y: number, r: number, base: string, edge: string) => {
    const g = ctx.createRadialGradient(x - r * 0.35, y - r * 0.35, r * 0.12, x, y, r)
    g.addColorStop(0, '#ffffff'); g.addColorStop(0.4, base); g.addColorStop(1, edge)
    ctx.fillStyle = g; ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2); ctx.fill()
  }

  const draw = useCallback(() => {
    const ctx = prepCanvas(cv.current, W, H); if (!ctx) return
    ctx.clearRect(0, 0, W, H)
    const a = p1.current, b = p2.current
    // đường thẳng nối tâm hai điện tích (giá của lực Coulomb)
    ctx.strokeStyle = 'rgba(100,116,139,0.55)'; ctx.lineWidth = 1.5; ctx.setLineDash([7, 5])
    ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y); ctx.stroke(); ctx.setLineDash([])
    const dx = b.x - a.x, dy = b.y - a.y
    const r = Math.max(30, Math.hypot(dx, dy))
    const F = (K * Math.abs(q1 * q2)) / (r * r)
    const ux = dx / r, uy = dy / r
    const attract = q1 * q2 < 0
    const fl = Math.min(175, 36 + F * 1.15)
    // lực trên q1: hút→hướng về q2 (+u); đẩy→ngược (−u)
    const s1 = attract ? 1 : -1
    arrow(ctx, a.x, a.y, ux * fl * s1, uy * fl * s1, '#7c3aed')
    arrow(ctx, b.x, b.y, -ux * fl * s1, -uy * fl * s1, '#7c3aed')
    // điện tích
    const r1 = 16 + Math.abs(q1) * 2, r2 = 16 + Math.abs(q2) * 2
    sphere(ctx, a.x, a.y, r1, q1 >= 0 ? '#f87171' : '#60a5fa', q1 >= 0 ? '#b91c1c' : '#1d4ed8')
    sphere(ctx, b.x, b.y, r2, q2 >= 0 ? '#f87171' : '#60a5fa', q2 >= 0 ? '#b91c1c' : '#1d4ed8')
    ctx.fillStyle = '#fff'; ctx.font = 'bold 16px sans-serif'; ctx.textAlign = 'center'
    ctx.fillText(q1 >= 0 ? '+' : '−', a.x, a.y + 6); ctx.fillText(q2 >= 0 ? '+' : '−', b.x, b.y + 6)
    // ký hiệu q₁, q₂ phía trên mỗi điện tích
    ctx.fillStyle = '#334155'; ctx.font = 'bold 15px sans-serif'
    ctx.fillText('q₁', a.x, a.y - r1 - 8); ctx.fillText('q₂', b.x, b.y - r2 - 8)
    ctx.textAlign = 'left'
    // số liệu
    ctx.fillStyle = '#334155'; ctx.font = 'bold 13px sans-serif'
    ctx.fillText(`r = ${r.toFixed(0)}   F ≈ ${F.toFixed(1)} (đv)   ${attract ? 'HÚT' : 'ĐẨY'}`, 16, 24)
  }, [q1, q2, K])
  useEffect(() => { draw() }, [draw])
  // Slider r: đặt 2 điện tích đối xứng theo phương ngang, cách nhau rVal
  useEffect(() => {
    const cx = W / 2, cy = H / 2
    p1.current = { x: cx - rVal / 2, y: cy }; p2.current = { x: cx + rVal / 2, y: cy }
    force((n) => n + 1); draw()
  }, [rVal, draw])

  const pos = (e: React.PointerEvent) => {
    const rect = cv.current!.getBoundingClientRect()
    return { x: (e.clientX - rect.left) * (W / rect.width), y: (e.clientY - rect.top) * (H / rect.height) }
  }
  const down = (e: React.PointerEvent) => {
    const p = pos(e)
    if (Math.hypot(p.x - p1.current.x, p.y - p1.current.y) < 30) drag.current = 1
    else if (Math.hypot(p.x - p2.current.x, p.y - p2.current.y) < 30) drag.current = 2
    if (drag.current && !firstRef.current) { firstRef.current = true; onComplete?.() }
  }
  const move = (e: React.PointerEvent) => {
    if (!drag.current) return
    const p = pos(e); const t = drag.current === 1 ? p1 : p2
    t.current = { x: Math.max(20, Math.min(W - 20, p.x)), y: Math.max(20, Math.min(H - 20, p.y)) }
    force((n) => n + 1); draw()
  }
  const up = () => { drag.current = null }

  return (
    <div className="rounded-2xl border border-violet-200 bg-white p-4 my-3">
      <h4 className="font-bold text-violet-800">{title}</h4>
      <p className="text-sm text-gray-500 mb-3">{description}</p>
      <canvas ref={cv} width={W} height={H} onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerLeave={up}
        className="w-full rounded-lg border border-gray-100 bg-slate-50 touch-none cursor-grab" />
      <div className="grid grid-cols-2 gap-3 mt-3">
        <label className="flex items-center gap-2 text-sm"><span className="w-16 text-gray-600">q₁</span>
          <input type="range" min={-6} max={6} step={1} value={q1} onChange={(e) => setQ1(Number(e.target.value))} className="flex-1" /><span className="w-10 text-right font-semibold">{q1 > 0 ? '+' : ''}{q1}</span></label>
        <label className="flex items-center gap-2 text-sm"><span className="w-16 text-gray-600">q₂</span>
          <input type="range" min={-6} max={6} step={1} value={q2} onChange={(e) => setQ2(Number(e.target.value))} className="flex-1" /><span className="w-10 text-right font-semibold">{q2 > 0 ? '+' : ''}{q2}</span></label>
      </div>
      <label className="flex items-center gap-2 text-sm mt-2"><span className="w-32 text-gray-600">Khoảng cách r</span>
        <input type="range" min={90} max={460} step={10} value={rVal} onChange={(e) => { firstRef.current || (firstRef.current = true, onComplete?.()); setRVal(Number(e.target.value)) }} className="flex-1 accent-violet-500" /><span className="w-12 text-right font-semibold">{rVal}</span></label>
    </div>
  )
}
export default CoulombForceSimulation
